const PREVIEW_VERSION = '1';
const MAX_PREVIEW_BYTES = 2 * 1024 * 1024;
const PREVIEW_WIDTH = 1200;
const PREVIEW_HEIGHT = 960;
const PRODUCTION_ENDPOINT = 'https://nwp-sleeve-studio.flux-42d.workers.dev/studio/cart-preview/v1';
const APPAREL_ENDPOINT = 'https://nwp-apparel-studio.flux-42d.workers.dev/studio/cart-preview/v1';
const CANOPY_ENDPOINT = 'https://nwp-canopy-studio.flux-42d.workers.dev/studio/cart-preview/v1';
const VALANCE_ENDPOINT = 'https://nwp-valance-studio.flux-42d.workers.dev/studio/cart-preview/v1';
const CANOPY_BANNER_ENDPOINT =
  'https://nwp-canopy-banner-studio.flux-42d.workers.dev/studio/cart-preview/v1';
const FABRIC_BANNER_ENDPOINT =
  'https://nwp-banner-studio.flux-42d.workers.dev/studio/cart-preview/v1';
// The server independently binds the signed receipt to the catalog variant and
// verifies the proof bytes. The cart's native Size option selects its exact
// 25-PPI approval-proof dimensions here; arbitrary dimensions are never accepted.
const FABRIC_PROOF_GEOMETRY = new Map([
  ['18 Inches by 36 inches', { width: 900, height: 450 }],
  ['18 Inches by 60 inches', { width: 1500, height: 450 }],
  ['24 Inches by 36 inches', { width: 900, height: 600 }],
  ['24 Inches by 60 inches', { width: 1500, height: 600 }],
  ['24 Inches by 72 inches', { width: 1800, height: 600 }],
  ['36 Inches by 60 inches', { width: 1500, height: 900 }],
  ['36 Inches by 96 inches', { width: 2400, height: 900 }],
  ['36 Inches by 120 inches', { width: 3000, height: 900 }],
]);
const RECEIPT =
  /^[a-z0-9]{16,40}\/(?:sd|ad|cd|vd|vb|bd)_[a-z0-9]{6,32}\/[a-zA-Z0-9_-]{1,24}\.[0-9a-f]{64}$/;
const previewCache = new Map();

function previewGeometry(endpoint, designId, variantId, size) {
  if (designId.startsWith('bd_')) {
    if (
      (endpoint !== FABRIC_BANNER_ENDPOINT && new URL(endpoint).origin !== location.origin) ||
      !/^\d+$/.test(variantId)
    )
      return null;
    return FABRIC_PROOF_GEOMETRY.get(size) || null;
  }
  if (endpoint === FABRIC_BANNER_ENDPOINT) return null;
  if (!designId.startsWith('vb_'))
    return endpoint === CANOPY_BANNER_ENDPOINT
      ? null
      : { width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT };
  if (endpoint !== CANOPY_BANNER_ENDPOINT && new URL(endpoint).origin !== location.origin)
    return null;
  if (variantId === '48685895680241') return { width: 2950, height: 300 };
  if (variantId === '67152275702001') return { width: 3000, height: 600 };
  return null;
}

function permittedEndpoint(value) {
  try {
    const endpoint = new URL(value);
    if (
      [
        PRODUCTION_ENDPOINT,
        APPAREL_ENDPOINT,
        CANOPY_ENDPOINT,
        VALANCE_ENDPOINT,
        CANOPY_BANNER_ENDPOINT,
        FABRIC_BANNER_ENDPOINT,
      ].includes(endpoint.href)
    )
      return endpoint.href;
    const localPage = ['localhost', '127.0.0.1', '::1'].includes(location.hostname);
    return localPage && endpoint.origin === location.origin ? endpoint.href : '';
  } catch {
    return '';
  }
}

async function boundedPreview(response) {
  if (!response.ok || response.redirected) throw new Error('preview_unavailable');
  if ((response.headers.get('Content-Type') || '').split(';')[0].trim() !== 'image/jpeg')
    throw new Error('preview_type');
  const declared = Number(response.headers.get('Content-Length') || 0);
  if (declared > MAX_PREVIEW_BYTES) throw new Error('preview_size');
  const reader = response.body?.getReader();
  if (!reader) throw new Error('preview_body');
  const chunks = [];
  let size = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      size += value.byteLength;
      if (size > MAX_PREVIEW_BYTES) {
        await reader.cancel();
        throw new Error('preview_size');
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }
  if (!size || (declared && declared !== size)) throw new Error('preview_size');
  return new Blob(chunks, { type: 'image/jpeg' });
}

async function validateImage(blob, geometry) {
  const url = URL.createObjectURL(blob);
  const image = new Image();
  try {
    image.src = url;
    if (typeof image.decode === 'function') await image.decode();
    else
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = reject;
      });
    if (image.naturalWidth !== geometry.width || image.naturalHeight !== geometry.height)
      throw new Error('preview_geometry');
  } finally {
    URL.revokeObjectURL(url);
  }
}

function cachedPreview(endpoint, receipt, designId, variantId, size) {
  const geometry = previewGeometry(endpoint, designId, variantId, size);
  if (!geometry) throw new Error('preview_geometry');
  const key = `${endpoint}\n${receipt}\n${designId}\n${variantId}\n${size}`;
  if (previewCache.has(key)) return previewCache.get(key);
  // A cart cannot legitimately contain an unbounded number of unique custom
  // sleeve lines. Keep page-lifetime deduplication bounded under hostile DOM use.
  if (previewCache.size >= 50) previewCache.delete(previewCache.keys().next().value);
  const controller = new AbortController();
  // The same-origin timeout is intentionally shorter for the isolated test
  // harness; production always uses the exact HTTPS endpoint above.
  const timeout = [
    PRODUCTION_ENDPOINT,
    APPAREL_ENDPOINT,
    CANOPY_ENDPOINT,
    VALANCE_ENDPOINT,
    CANOPY_BANNER_ENDPOINT,
    FABRIC_BANNER_ENDPOINT,
  ].includes(endpoint)
    ? 10000
    : 750;
  const timer = setTimeout(() => controller.abort(), timeout);
  const pending = fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-NWP-Preview': PREVIEW_VERSION,
    },
    body: JSON.stringify({ receipt, designId, variantId }),
    credentials: 'omit',
    redirect: 'error',
    referrerPolicy: 'no-referrer',
    signal: controller.signal,
  })
    .then(boundedPreview)
    .then(async (blob) => {
      await validateImage(blob, geometry);
      return { blob, ...geometry };
    })
    .catch((error) => {
      previewCache.delete(key);
      throw error;
    })
    .finally(() => clearTimeout(timer));
  previewCache.set(key, pending);
  return pending;
}

class NwpSleeveCartMedia extends HTMLElement {
  connectedCallback() {
    if (this.previewUrl || this.loading) return;
    const receipt = String(this.dataset.receipt || '');
    const designId = String(this.dataset.designId || '');
    const variantId = String(this.dataset.variantId || '');
    const size = String(this.dataset.previewSize || '');
    const endpoint = permittedEndpoint(this.dataset.endpoint || PRODUCTION_ENDPOINT);
    const image = this.querySelector('img');
    if (
      !endpoint ||
      !RECEIPT.test(receipt) ||
      !image ||
      !previewGeometry(endpoint, designId, variantId, size)
    )
      return;
    const start = () => {
      this.observer?.disconnect();
      this.observer = null;
      if (!this.isConnected || this.previewUrl || this.loading) return;
      const generation = (this.generation || 0) + 1;
      this.generation = generation;
      this.loadWithRetry(endpoint, receipt, designId, variantId, size, image, generation);
    };
    if (typeof IntersectionObserver !== 'function') start();
    else {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) start();
      });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
    this.generation = (this.generation || 0) + 1;
    this.loading = false;
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = '';
  }

  async loadWithRetry(endpoint, receipt, designId, variantId, size, image, generation) {
    this.loading = true;
    this.dataset.previewState = 'loading';
    this.setAttribute('aria-busy', 'true');
    try {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          await this.load(endpoint, receipt, designId, variantId, size, image, generation);
          if (generation === this.generation) this.dataset.previewState = 'ready';
          return;
        } catch {
          if (generation !== this.generation || !this.isConnected) return;
          if (attempt === 1) this.dataset.previewState = 'fallback';
          else await new Promise((resolve) => setTimeout(resolve, 750));
        }
      }
    } finally {
      if (generation === this.generation) {
        this.loading = false;
        this.removeAttribute('aria-busy');
      }
    }
  }

  async load(endpoint, receipt, designId, variantId, size, image, generation) {
    const { blob, width, height } = await cachedPreview(
      endpoint,
      receipt,
      designId,
      variantId,
      size,
    );
    if (generation !== this.generation || !this.isConnected || image !== this.querySelector('img'))
      return;
    const url = URL.createObjectURL(blob);
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
    this.previewUrl = url;
    image.srcset = '';
    image.sizes = '';
    image.src = url;
    image.width = width;
    image.height = height;
    image.alt = this.dataset.previewAlt || image.alt;
    image.dataset.nwpSleeveCartPreview = '';
  }
}

if (!customElements.get('nwp-sleeve-cart-media'))
  customElements.define('nwp-sleeve-cart-media', NwpSleeveCartMedia);
